/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import { groupBy } from 'lodash';

import { ICartState } from '@msdyn365-commerce/global-state';
import { msdyn365Commerce, getUrlSync } from '@msdyn365-commerce/core-internal';
import { urlCheck, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';

import { EveQuickOrderItemsInput, IEveQuickOrderItemsData } from '../../actions/eve-quick-order-items.action';
import { IEveQuickOrderData } from './eve-quick-order.data';

import { EveQuickOrderControls } from './components/eve-quick-order-controls';
import { EveQuickOrderRow } from './components/eve-quick-order-row';
import { IEveQuickOrderProps as _IEveQuickOrderProps } from './eve-quick-order.props.autogenerated';

/**
 * Quick Order View Props Interface properties.
 */
export interface IEveQuickOrderViewProps extends _IEveQuickOrderProps<IEveQuickOrderData> {
    isB2B?: boolean;
    contentProps: INodeProps;
    quickOrderProps: IModuleProps;
    quickOrderList: React.ReactNode;
    orderUpload: React.ReactNode;
    rfq: React.ReactNode;
}

/**
 * Quick Order Interface properties.
 */
export interface IEveQuickOrderState {
    rows: IEveQuickOrderItemsData['items'];
    useLadvigeMobile: boolean;
}

export interface IEveQuickOrderProps extends _IEveQuickOrderProps<IEveQuickOrderData> {}

/**
 * QuickOrder component.
 * @extends {React.PureComponent<IEveQuickOrderProps<IEveQuickOrderData>, IEveQuickOrderState>}
 */
class EveQuickOrder extends React.PureComponent<IEveQuickOrderProps, IEveQuickOrderState> {
    private readonly baseProps = { 'data-ignore-parent-css': true };

    constructor(props: IEveQuickOrderProps) {
        super(props);

        const { items } = props.data.quickOrderItems.result ?? {};
        const rows = items ?? [];

        this.state = { rows, useLadvigeMobile: innerWidth >= 480 && innerWidth <= 992 };
        this._removeRow = this._removeRow.bind(this);
    }

    public readonly componentDidMount = () => {
        window.onresize = () => this.setState({ useLadvigeMobile: innerWidth >= 480 && innerWidth <= 992 });
    };

    public readonly componentDidUpdate = () => {
        const { items } = this.props.data.quickOrderItems.result ?? {};
        const rows = items ?? [];
        this.setState(state => ({ ...state, rows }));
    };

    // @ts-ignore
    private readonly _getCheckoutURL = (isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string) => {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const isAbsoluteUrl = urlCheck(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (msdyn365Commerce.isBrowser && !isAbsoluteUrl) {
            returnURL = `${location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    };

    private readonly _removeRow = (index: number) => {
        if (this.state.rows.length > 1) {
            const rows = this.state.rows.slice(0, index).concat(this.state.rows.slice(index + 1));
            this.setState(state => ({ ...state, rows }));
            this.props.context.actionContext.update<IEveQuickOrderItemsData>(new EveQuickOrderItemsInput(), { items: rows });
        }
    };

    private readonly _addRow = () => {
        const rows = this.state.rows.concat(this._createEmptyRows(1));
        this.setState(state => ({ ...state, rows }));
        this.props.context.actionContext.update<IEveQuickOrderItemsData>(new EveQuickOrderItemsInput(), { items: rows });
    };

    private readonly _createEmptyRows = (amount = 1) => {
        return new Array<IEveQuickOrderItemsData['items'][0]>(amount).fill({ itemNumber: '', quantity: 0 });
    };

    /**
     * Method to add product in cart.
     */
    private readonly _addItemToCart = async () => {
        const { context, data } = this.props;
        const { catalogId } = context.request.apiSettings;
        const { actionContext } = context;
        const { cart } = data;

        const productsToAdd: Parameters<ICartState['addProductsToCart']>[0] = this.state.rows
            .filter(
                ({ itemNumber, quantity, selectedVariant }) =>
                    itemNumber.length && quantity > 0 && selectedVariant?.DistinctProductVariantId
            )
            .map(({ selectedVariant, quantity }) => ({
                product: {
                    ...selectedVariant!,
                    ProductTypeValue: 0,
                    RecordId: selectedVariant!.DistinctProductVariantId!
                },
                count: quantity,
                catalogId
            }));

        const dedupedProducsToAdd = this._dedupeCartProducts(productsToAdd);

        if (cart.result && dedupedProducsToAdd.length) {
            const groupedDeduped = Object.values(groupBy(dedupedProducsToAdd, 'product.RecordId'));
            const finalProducts = groupedDeduped.map(group =>
                // @ts-expect-error
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                group.reduce((prev, cur) => ({ ...prev, count: (prev.count ?? 0) + (cur.count ?? 0) }))
            );
            cart.result.addProductsToCart(finalProducts).catch(() => {});
        }
        if (productsToAdd.length) {
            const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
            location.href = guestCheckoutUrl;
        }
        actionContext.update<IEveQuickOrderItemsData>(new EveQuickOrderItemsInput(), {
            items:
                this.props.data.quickOrderItems.result?.items.map(() => ({ itemNumber: '', quantity: 0 })) ??
                new Array<IEveQuickOrderItemsData['items'][0]>(9).fill({ itemNumber: '', quantity: 0 })
        });
    };

    private readonly _dedupeCartProducts = (productsToAdd: Parameters<ICartState['addProductsToCart']>[0]) => {
        const { cart } = this.props.data.cart.result ?? {};
        const productsInCart = cart?.CartLines?.map(({ ProductId }) => ProductId);
        const productsToUpdate = productsToAdd
            .filter(({ product }) => productsInCart?.includes(product.RecordId))
            .map(product => {
                const cartLine = cart?.CartLines?.filter(({ ProductId }) => ProductId === product.product.RecordId)[0] ?? {};
                return { ...product, cartLineId: cartLine.LineId, quantityInCart: cartLine.Quantity };
            });
        const filteredProductsToAdd = productsToAdd.filter(({ product }) => !productsInCart?.includes(product.RecordId));
        for (const { count, cartLineId, quantityInCart } of productsToUpdate) {
            if (cartLineId && quantityInCart && count) {
                console.log(cartLineId);
                this.props.data.cart.result
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    ?.updateCartLineQuantity?.({ cartLineId, newQuantity: quantityInCart + count })
                    .then(result => console.log(result))
                    .catch(() => {});
            }
        }
        return filteredProductsToAdd;
    };

    private readonly _renderRFQ = () => {
        const cn = (suffix: string) => `cps-rfq${suffix}`;
        return (
            <div {...this.baseProps} className={cn('')}>
                <div {...this.baseProps} className={cn('__wrapper')}>
                    RFQ
                </div>
            </div>
        );
    };

    /**
     * Render Quick Order Table.
     * @param className - Class name.
     * @returns JSX element.
     */
    private readonly _renderOrderUpload = () => {
        return this.props.slots.orderUpload[0];
    };

    /**
     * Render Quick Order Table.
     * @param className - Class name.
     * @returns JSX element.
     */
    private readonly _renderQuickOrderTable = () => {
        const { resources, context } = this.props;
        const headerColumns = [resources.tableHeaderItemNumber, resources.tableHeaderConfiguration, resources.tableHeaderQuantity];
        const lavidgeHeaderColumns = [
            `${resources.tableHeaderItemNumber}/${resources.tableHeaderConfiguration}`,
            resources.tableHeaderQuantity,
            `<div data-ignore-parent-css></div>`
        ];

        const cn = (suffix: string) => `cps-quick-order__table${suffix}`;

        if (this.state.useLadvigeMobile) {
            return (
                <table {...this.baseProps} id={cn('--lavidge-mobile')} cellSpacing={0} className={cn('--lavidge-mobile')}>
                    <thead {...this.baseProps}>
                        <tr {...this.baseProps}>
                            {lavidgeHeaderColumns.map(label => (
                                <th key={label} {...this.baseProps} dangerouslySetInnerHTML={{ __html: label }}></th>
                            ))}
                        </tr>
                    </thead>
                    <tbody {...this.baseProps}>
                        {this.state.rows.map((initialValues, rowIndex) => (
                            <EveQuickOrderRow
                                {...this.props}
                                isLavidgeMobile
                                allRows={this.state.rows}
                                context={context}
                                initialValues={initialValues}
                                key={initialValues.itemNumber + rowIndex.toString()}
                                removeRow={() => this._removeRow(rowIndex)}
                                resources={resources}
                                rowIndex={rowIndex}
                            />
                        ))}
                    </tbody>
                    <EveQuickOrderControls
                        canAddToCart={1}
                        isLavidgeMobile
                        handleAddRow={this._addRow}
                        handleAddToCart={this._addItemToCart}
                        resources={this.props.resources}
                    />
                </table>
            );
        }

        return (
            <div {...this.baseProps} id={cn('')} className={cn('')}>
                <div {...this.baseProps}>
                    <div {...this.baseProps} className={cn('-header')}>
                        {headerColumns.map((title, i) => (
                            <div {...this.baseProps} key={title} className={cn(`-header--cell-${i}`)}>
                                {title.toUpperCase()}
                            </div>
                        ))}
                    </div>
                    <div {...this.baseProps} className={cn('-body')}>
                        {this.state.rows.map((initialValues, rowIndex) => (
                            <EveQuickOrderRow
                                {...this.props}
                                allRows={this.state.rows}
                                context={context}
                                initialValues={initialValues}
                                key={initialValues.itemNumber + rowIndex.toString()}
                                removeRow={() => this._removeRow(rowIndex)}
                                resources={resources}
                                rowIndex={rowIndex}
                            />
                        ))}
                    </div>
                    <EveQuickOrderControls
                        canAddToCart={1}
                        handleAddRow={this._addRow}
                        handleAddToCart={this._addItemToCart}
                        resources={this.props.resources}
                    />
                </div>
            </div>
        );
    };

    public readonly render = () => {
        const { className = '' } = this.props.config;
        const customerInfo = this.props.data.customerInformation.result;

        const viewProps: IEveQuickOrderViewProps = {
            ...(this.props as IEveQuickOrderProps),
            contentProps: { className: 'ms-quick-order__content' },
            isB2B: customerInfo?.IsB2b,
            orderUpload: this._renderOrderUpload(),
            quickOrderList: this._renderQuickOrderTable(),
            quickOrderProps: { className: classnames('ms-quick-order', className), moduleProps: this.props },
            rfq: this._renderRFQ()
        };

        return this.props.renderView(viewProps);
    };
}

export default EveQuickOrder;
